import {
  type AddProcurationRequest,
  type ApproveProcurationRequest,
  type RemoveProcurationRequest,
  type UpdateProcurationRequest,
  type UpdateProcurationSignature,
} from '~/common/types/meeting/meetingProcurationRequests';
import {
  type CreateAgendaPointRequest,
  type CreateMeetingRequest,
  type DeleteAgendaPointRequest,
  type RegisterAttendeesRequest,
  type UpdateAgendaPointRequest,
  type UpdateMeetingRequest,
  type UpdateMeetingStatusRequest,
} from '~/common/types/meeting/meetingRequests';
import {
  type CreateMeetingTaskRequest,
  type MeetingTask,
  type UpdateMeetingTaskRequest,
} from '~/common/types/meeting/meetingTasks';
import {
  type InviteAttendeeRequest,
  type UpdateAttendeeInvitationStatusRequest,
  type UpdateAttendeeRemarkRequest,
} from '~/common/types/meeting/meetingAttendeeRequest';
import { type ExtendedMeeting } from '@/types/meeting';
import { type Meeting } from '~/common/types/meeting/meeting';
import { type MeetingAttendee } from '~/common/types/meeting/meetingCommunications';
import { type MeetingProcurationHolderResponse } from '~/common/types/meeting/meetingProcurations';

import config from '@/config/config';

import createRestClient from '../restClient';
import { objectToFormData } from '@/shared/utils/helpers';

const client = createRestClient(config.baseURL);

const getMeeting = (meetingId: string): Promise<ExtendedMeeting> =>
  client.get(`/meeting/${meetingId}`);

const getMeetingsForHomeownerAssociation = (homeownerAssociationId: string): Promise<Meeting[]> =>
  client.get(`/meeting/hoa/${homeownerAssociationId}`);

const deleteMeeting = (id: string) => client.delete(`/meeting/${id}`);

const createMeeting = (data: CreateMeetingRequest): Promise<Meeting> =>
  client.post('/meeting', data);

const updateMeeting = (data: UpdateMeetingRequest) => client.put('/meeting', data);

const updateMeetingStatus = (data: UpdateMeetingStatusRequest) =>
  client.put('/meeting/status', data);

const deleteAgendaPoint = (data: DeleteAgendaPointRequest) =>
  client.delete(`/meeting/agenda-point/${data.id}`, { data });

const restClient = createRestClient();

const createAgendaPoint = (data: CreateAgendaPointRequest) =>
  restClient.post('meeting/agenda-point', objectToFormData(data));

const updateAgendaPoint = (data: UpdateAgendaPointRequest) =>
  restClient.put('meeting/agenda-point', objectToFormData(data));

const getMeetingTasks = (
  meetingId: string,
  homeownerAssociationId: string,
): Promise<MeetingTask[]> =>
  restClient.get(`/meeting/${meetingId}/task`, { params: { homeownerAssociationId } });

const createMeetingTask = (data: CreateMeetingTaskRequest) =>
  restClient.post(`/meeting/${data.meetingId}/task`, data);

const updateMeetingTask = (data: UpdateMeetingTaskRequest) =>
  restClient.put(`/meeting/${data.meetingId}/task`, data);

const registerAttendee = (data: RegisterAttendeesRequest) =>
  client.put(`/meeting/attendee-register`, data);

const getAttendees = (
  meetingId: string,
  homeownerAssociationId: string,
): Promise<MeetingAttendee[]> =>
  client.get(`/meeting/${meetingId}/attendee.list`, { params: { homeownerAssociationId } });

const getProcurationHolders = (
  meetingId: string,
  divisionKeyId: string,
): Promise<MeetingProcurationHolderResponse[]> =>
  client.get(`/meeting/${meetingId}/procuration-holder.list`, { params: { divisionKeyId } });

const getPossibleBuildingRelations = (
  meetingId: string,
  homeownerAssociationId: string,
): Promise<
  {
    id: string;
    contactId: string;
    companyId: string;
    displayName: string;
    buildingId: string;
    relationId: string;
  }[]
> =>
  client.get(`/meeting/${meetingId}/possibleBuildingRelations`, {
    params: { homeownerAssociationId },
  });

const inviteAttendee = (data: InviteAttendeeRequest) => client.post('/meeting/attendee', data);

const updateAttendeeInvitationStatus = (data: UpdateAttendeeInvitationStatusRequest) =>
  client.put('/meeting/attendee/invitation-status', data);

const updateAttendeeRemark = (data: UpdateAttendeeRemarkRequest) =>
  client.put('/meeting/attendee/remark', data);

const addProcuration = (data: AddProcurationRequest) =>
  client.post('/meeting/procuration', objectToFormData(data), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const approveProcuration = (data: ApproveProcurationRequest) =>
  client.put('/meeting/procuration/approve', data);

const updateProcuration = (data: UpdateProcurationRequest) =>
  client.put('/meeting/procuration', objectToFormData(data), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const updateProcurationSignature = (data: UpdateProcurationSignature) =>
  restClient.put('/meeting/procuration/signature', objectToFormData(data), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const removeProcuration = (data: RemoveProcurationRequest) =>
  client.delete('/meeting/procuration/remove', { data });

export default {
  addProcuration,
  approveProcuration,
  createAgendaPoint,
  createMeeting,
  createMeetingTask,
  deleteAgendaPoint,
  deleteMeeting,
  getAttendees,
  getMeeting,
  getMeetingsForHomeownerAssociation,
  getMeetingTasks,
  getPossibleBuildingRelations,
  getProcurationHolders,
  inviteAttendee,
  registerAttendee,
  removeProcuration,
  updateAgendaPoint,
  updateAttendeeInvitationStatus,
  updateAttendeeRemark,
  updateMeeting,
  updateMeetingStatus,
  updateMeetingTask,
  updateProcuration,
  updateProcurationSignature,
};
